import { atom } from 'jotai'
import { atomFamily } from 'jotai-family'
import { atomWithStorage } from 'jotai/utils'

import { STORAGE_KEYS } from '@/lib/configs/storage-config'
import { safeStorage } from '@/lib/safe-storage'

export type LinkingColor =
  | 'unlinked'
  | 'mint'
  | 'yellow'
  | 'orange'
  | 'pink'
  | 'purple'
  | 'blue'
  | 'green'
export type GlowType = 'ticker-change' | 'color-change'
export type LinkingTickerType = 'perps' | 'spot' | 'future' | 'options'

export interface LinkingTickerInfo {
  symbol: string
  type: LinkingTickerType
  baseCurrency: string
  expiry?: string // For options/futures with expiration
}

// ========== Linking State Atoms ==========

// Store linking colors per page, isolated per dashboard tab
export const linkingColorsAtomFamily = atomFamily((_pageId: string) =>
  atom<Record<string, LinkingColor>>({})
)

/**
 * User-chosen names for link groups, keyed by color ("Majors" instead of "Mint"). One name per
 * color across all pages, since a color means the same group everywhere a trader looks. An
 * absent or blank entry falls back to the color's own name. Per-device, like the colors.
 */
export const linkingGroupNamesAtom = atomWithStorage<Partial<Record<LinkingColor, string>>>(
  STORAGE_KEYS.dashboard.linkingGroupNames,
  {},
  safeStorage
)

// Store glow triggers for cards - incremented to trigger glow effect
export const glowTriggersAtom = atom<Record<string, { count: number; type: GlowType }>>({})

// Store glow triggers by linking color
export const colorGlowTriggersAtom = atom<Record<LinkingColor, number>>({
  unlinked: 0,
  mint: 0,
  yellow: 0,
  orange: 0,
  pink: 0,
  purple: 0,
  blue: 0,
  green: 0,
})

// Store ticker change glow triggers by linking color
export const colorTickerGlowTriggersAtom = atom<Record<LinkingColor, number>>({
  unlinked: 0,
  mint: 0,
  yellow: 0,
  orange: 0,
  pink: 0,
  purple: 0,
  blue: 0,
  green: 0,
})

// Store selected ticker per linking group (by linking color)
// When a user clicks a ticker in a watch list, this stores the ticker info for that color group
export const linkingGroupTickersAtom = atom<Record<LinkingColor, LinkingTickerInfo | null>>({
  unlinked: null,
  mint: null,
  yellow: null,
  orange: null,
  pink: null,
  purple: null,
  blue: null,
  green: null,
})

export interface WatchListSearchQueryRequest {
  query: string
  // Incremented on every dispatch so requesting the same query twice in a row still
  // re-triggers the consuming WatchList's effect (mirrors focusedOptionStrike._id).
  requestId: number
}

// One-shot "type this into the watch-list search box" request per linking color.
// Other widgets (e.g. account balance table) dispatch here on click; WatchList applies
// it once and never writes back, so there's no ping-pong between widgets.
export const watchListSearchQueryRequestAtom = atom<
  Record<LinkingColor, WatchListSearchQueryRequest | null>
>({
  unlinked: null,
  mint: null,
  yellow: null,
  orange: null,
  pink: null,
  purple: null,
  blue: null,
  green: null,
})

// ========== Helper Functions ==========

/**
 * Trigger glow effect for a specific card
 * @param setGlowTriggers - Setter function from useSetAtom(glowTriggersAtom)
 * @param cardId - Unique ID of the card to trigger glow
 * @param type - Type of glow effect ('ticker-change' or 'color-change')
 */
export const triggerCardGlow = (
  setGlowTriggers: (
    fn: (
      prev: Record<string, { count: number; type: GlowType }>
    ) => Record<string, { count: number; type: GlowType }>
  ) => void,
  cardId: string,
  type: GlowType = 'ticker-change'
) => {
  setGlowTriggers((prev) => ({
    ...prev,
    [cardId]: {
      count: (prev[cardId]?.count ?? 0) + 1,
      type,
    },
  }))
}

/**
 * Set linking color for a specific card
 * @param setLinkingColors - Setter function from useSetAtom(linkingColorsAtom)
 * @param cardId - Unique ID of the card
 * @param color - Linking color to set
 */
export const setCardLinkingColor = (
  setLinkingColors: (
    fn: (prev: Record<string, LinkingColor>) => Record<string, LinkingColor>
  ) => void,
  cardId: string,
  color: LinkingColor
) => {
  setLinkingColors((prev) => ({
    ...prev,
    [cardId]: color,
  }))
}

/**
 * Trigger glow effect for all cards of a specific color
 */
export const triggerColorGlow = (
  setColorGlowTriggers: (
    fn: (prev: Record<LinkingColor, number>) => Record<LinkingColor, number>
  ) => void,
  color: LinkingColor
) => {
  setColorGlowTriggers((prev) => ({
    ...prev,
    [color]: (prev[color] ?? 0) + 1,
  }))
}

/**
 * Trigger ticker-change glow effect for all cards of a specific color
 */
export const triggerColorTickerGlow = (
  setColorTickerGlowTriggers: (
    fn: (prev: Record<LinkingColor, number>) => Record<LinkingColor, number>
  ) => void,
  color: LinkingColor
) => {
  setColorTickerGlowTriggers((prev) => ({
    ...prev,
    [color]: (prev[color] ?? 0) + 1,
  }))
}
